/*eslint-disable*/
import React, { useEffect, useRef } from 'react';
import { Form, Input, InputNumber, message, Tooltip } from 'antd';
import { NumericFormat } from 'react-number-format';
import { withValueCap } from 'pages/globals/global';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { processArrays, transformRangeDataToAmountArray } from '../LeaseUtils/helper';

const GrossRent = ({ screen = 'CREATE', field, index }) => {
	const form = Form.useFormInstance();
	const isUpdatingRef = useRef(false);

	const grossRentKey = useRef('');
	const grossRentList = Form.useWatch(['leaseSimulator', index, 'grossRentList'], form);

	// Handler for when an amount changes
	const handleAmountChange = (lsIndex, grIndex) => {
		// Automatically apply the changes
		const timeoutId = setTimeout(() => {
			handleApply(lsIndex);
		}, 10);

		return () => {
			clearTimeout(timeoutId);
		};
	};

	// Handler for "To" value changes
	const handleToChange = (lsIndex, grIndex, value) => {
		// console.log('🚀 ~ handleToChange ~ value:', value);
		// Automatically apply the changes
		setTimeout(() => handleApply(lsIndex), 10);
	};

	// Validate To value ONLY on blur to allow typing multi-digit numbers
	const validateToValueOnBlur = (lsIndex, grIndex) => {
		// Get the current values
		const fromValue = form.getFieldValue(['leaseSimulator', lsIndex, 'grossRentList', grIndex, 'from']);
		const toValue = form.getFieldValue(['leaseSimulator', lsIndex, 'grossRentList', grIndex, 'to']);

		// Validate that To is greater than or equal to From
		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
			message.error("'To' value must be greater than or equal to 'From' value");
			form.setFields([
				{
					name: ['leaseSimulator', lsIndex, 'grossRentList', grIndex, 'to'],
					value: '',
					errors: ["'To' value must be greater than 'From' value"],
				},
			]);
		}

		// Automatically apply the changes
		setTimeout(() => handleApply(lsIndex), 10);
	};

	// Validate if a row is complete and valid
	const isRowValid = (fromValue, toValue, amount) => {
		if (isNaN(toValue) || toValue <= 0) return false;
		if (isNaN(fromValue) || fromValue <= 0) return false;
		if (toValue < fromValue) return false;
		if (!amount) return false;

		return true;
	};

	// Handle adding a new row - this now adds a row after the current one
	const handleAddRow = (lsIndex, grIndex) => {
		const fromValue = parseInt(form.getFieldValue(['leaseSimulator', lsIndex, 'grossRentList', grIndex, 'from']));
		const toValue = parseInt(form.getFieldValue(['leaseSimulator', lsIndex, 'grossRentList', grIndex, 'to']));
		const amount = parseInt(form.getFieldValue(['leaseSimulator', lsIndex, 'grossRentList', grIndex, 'amount']));

		// console.log(fromValue, toValue, amount);

		// Check if the current row is complete
		if (!isRowValid(fromValue, toValue, amount)) {
			message.error('Please complete all required fields in the current row before adding a new one');
			return;
		}

		const gl = form.getFieldValue(['leaseSimulator', lsIndex, 'grossRentList']) || [];
		form.setFieldValue(['leaseSimulator', lsIndex, 'grossRentList'], [...gl, { from: toValue + 1, to: null, Amount: null }]);

		// Automatically apply the changes
		setTimeout(() => handleApply(lsIndex), 10);
	};

	const handleDeleteRow = (lsIndex) => {
		// Automatically apply the changes
		setTimeout(() => handleApply(lsIndex), 10);
	};

	const handleApply = (lsIndex) => {
		// Collect all form values for gross rent rows
		const currentGrossValues = form.getFieldValue(['leaseSimulator', lsIndex, 'grossRentList']);

		// console.log('currentGrossValues', currentGrossValues);
		if (!currentGrossValues || currentGrossValues.length === 0) return;
		const transformedData = transformRangeDataToAmountArray(currentGrossValues);
		// console.log('transformedData', transformedData);
		form.setFieldValue(['leaseSimulator', lsIndex, 'grossRents'], transformedData);

		const currentAdvanceValues = form.getFieldValue(['leaseSimulator', lsIndex, 'advanceAmountList']);
		if (!currentAdvanceValues || currentAdvanceValues?.length === 0) return;
		const transformedDataAdvanceAmounts = transformRangeDataToAmountArray(currentAdvanceValues);
		const { processedAdvanceAmounts } = processArrays(transformedData, transformedDataAdvanceAmounts);
		form.setFieldValue(['leaseSimulator', index, 'advanceAmounts'], processedAdvanceAmounts);
	};

	useEffect(() => {
		if (!Array.isArray(grossRentList) || grossRentList.length === 0) return;

		// Generate keys for comparison
		const newGrossRentKey = grossRentList.map((item) => `${item.from}-${item.to}`).join('|');

		// Skip if no changes in the structure
		if (newGrossRentKey === grossRentKey.current) return;

		// Prevent updates if we're already in an update cycle
		if (isUpdatingRef.current) return;

		// Set flag to indicate we're updating
		isUpdatingRef.current = true;

		try {
			// Update ref values
			grossRentKey.current = newGrossRentKey;
			const maxTerm = Math.max(...grossRentList.map((item) => item.to ?? 0));
			// console.log('maxTerm', maxTerm);
			// Check if the value actually needs to be updated
			const currentLeaseTerm = form.getFieldValue(['leaseSimulator', index, 'leaseTerm']);
			if (currentLeaseTerm !== maxTerm) {
				form.setFieldValue(['leaseSimulator', index, 'leaseTerm'], maxTerm);
				// if (screen?.toLowerCase() === 'revise') {
				// 	const currentChanges = form.getFieldValue('changes');
				// 	if (currentChanges?.length > 0) {
				// 		form.setFieldValue(['changes', currentChanges.length - 1, 'inputChangeLeaseTerm'], maxTerm);
				// 	}
				// }

				// if (screen?.toLowerCase() === 'create') {
				// 	const currentChangeLeaseTerm = form.getFieldValue(['changes', 0, 'inputChangeLeaseTerm']);
				// 	if (currentChangeLeaseTerm !== maxTerm) {
				// 		form.setFieldValue(['changes', 0, 'inputChangeLeaseTerm'], maxTerm);
				// 	}
				// }
			}

			// const leaseStartDate = form.getFieldValue('leaseStartDate');

			// if (leaseStartDate && maxTerm) {
			// 	const leaseEndDate = leaseStartDate.add(maxTerm, 'month').subtract(1, 'day');
			// 	const currentLeaseEndDate = form.getFieldValue('leaseEndDate');

			// 	// Only update if the value has actually changed
			// 	if (!currentLeaseEndDate || !leaseEndDate.isSame(currentLeaseEndDate)) {
			// 		form.setFieldValue('leaseEndDate', leaseEndDate);
			// 	}
			// }
		} finally {
			// Reset the flag after update is complete

			setTimeout(() => {
				isUpdatingRef.current = false;
			}, 0);
		}
	}, [grossRentList]);

	// Initialize with the first row
	useEffect(() => {
		// Automatically apply initial data
		handleApply(index);
	}, [form, index]);

	return (
		<>
			<Form.List name={[field.name, 'grossRentList']}>
				{(subFields, subFieldsOpt) => (
					<TableContainer
						className="gross-rent-table"
						sx={{
							'& table, & th, & td': {
								border: '1px solid #e0e0e0',
								borderCollapse: 'collapse',
							},
							'& th, & td': {
								p: '5px',
							},
							'& td .ant-form-item': {
								marginBottom: '0px',
							},

							'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '25%' } /* From/From Month */,
							'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '25%' } /* To/To Month */,
							'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '25%' } /* Amount/Monthly Adjustment */,
							// '& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' } /* Remarks/Total Amount */,
							'& th:nth-of-type(5), & td:nth-of-type(4)': { width: '25%' } /* Action */,
							'& table': { width: '100%' } /* Ensure table takes full container width */,
						}}
					>
						<Table
							aria-label="Gross Rent table"
							sx={{
								'& .MuiTableBody-root .MuiTableCell-root': {
									px: 0.5,
									// mx: 0,
									// py: 0.5,
									py: 0.5,
								},
								'& .MuiTableBody-root .MuiTableRow-root': {
									borderBottom: '0px solid #000',
								},
								tableLayout: 'fixed',
							}}
						>
							<TableHead>
								<TableRow sx={{ backgroundColor: '#ffffff' }}>
									<TableCell
										sx={{
											textAlign: 'center',
											backgroundColor: '#f5f5f5',
											// color: 'blue'
										}}
									>
										From Month
									</TableCell>
									<TableCell
										sx={{
											textAlign: 'center',
											backgroundColor: '#f5f5f5',
											//  color: 'blue'
										}}
									>
										To Month
									</TableCell>
									<TableCell
										sx={{
											textAlign: 'center',
											backgroundColor: '#f5f5f5',
											//  color: 'blue'
										}}
									>
										Amount
									</TableCell>
									{/* <TableCell sx={{ textAlign: 'center' ,backgroundColor: '#f5f5f5',
											color: 'blue'

											}}>Notes</TableCell> */}
									{screen !== 'VIEW' && (
										<TableCell
											sx={{
												textAlign: 'center',
												backgroundColor: '#f5f5f5',
												//  color: 'blue'
											}}
										>
											Action
										</TableCell>
									)}
								</TableRow>
							</TableHead>

							<TableBody>
								{subFields.length > 0 ? (
									subFields.map((subField, grIndex) => (
										<TableRow key={grIndex}>
											<TableCell>
												<Form.Item name={[subField.name, 'from']} initialValue={1}>
													<InputNumber
														disabled={true} // Always disabled to prevent manual editing
														style={{
															width: '100%',
															backgroundColor: '#ffffff', // Grey background to indicate disabled
															textAlign: 'center',
														}}
													/>
												</Form.Item>
											</TableCell>

											<TableCell>
												<Form.Item
													name={[subField.name, 'to']}
													rules={[
														{ required: true, message: "Please enter a 'To' value" },
														({ getFieldValue }) => ({
															validator(_, value) {
																// const fromValue = getFieldValue(`leaseSimulator${index}grossRentList${grIndex}From`);
																const fromValue = getFieldValue([
																	'leaseSimulator',
																	index,
																	'grossRentList',
																	grIndex,
																	'from',
																]);

																if (
																	value === undefined ||
																	value === null ||
																	value === false ||
																	isNaN(value) ||
																	value === '' ||
																	fromValue === undefined ||
																	fromValue === null ||
																	isNaN(fromValue) ||
																	fromValue === '' ||
																	fromValue === false
																) {
																	return Promise.resolve();
																}
																if (value < fromValue) {
																	return Promise.reject(new Error("'To' must be greater or equal than 'From'"));
																}
																return Promise.resolve();
															},
														}),
													]}
												>
													<InputNumber
														type="number"
														disabled={screen === 'VIEW'}
														placeholder="Enter To value"
														style={{
															width: '100%',
															textAlign: 'center',
														}}
														onChange={(value) => handleToChange(index, grIndex, value)}
														onBlur={() => validateToValueOnBlur(index, grIndex)}
													/>
												</Form.Item>
											</TableCell>

											<TableCell>
												<Form.Item
													name={[subField.name, 'amount']}
													rules={[{ required: true, message: 'Please enter an amount' }]}
													getValueFromEvent={(values) => {
														const input = values?.target?.value ?? '';
														const cleaned = input.replace(/,/g, ''); // Remove all commas
														const data = parseFloat(cleaned); // parseFloat handles decimals

														return isNaN(data) ? 0 : data;
													}}
												>
													<NumericFormat
														size="small"
														decimalScale={2}
														placeholder="0.00"
														fixedDecimalScale
														thousandSeparator=","
														isAllowed={withValueCap}
														style={{
															// padding: '18px',
															textAlign: 'right',
															height: '32px',
															borderRadius: '4px',
															fontSize: '13px',
														}}
														customInput={Input}
														disabled={screen === 'VIEW'}
														onValueChange={(values) => {
															handleAmountChange(index, grIndex);
														}}
														onBlur={() => setTimeout(() => handleApply(index), 10)}
													/>
												</Form.Item>
											</TableCell>
											{screen !== 'VIEW' && (
												<TableCell align="center">
													{/* Logic for displaying icons based on row position */}
													{/* First row with only one row in total - only plus icon */}
													{subFields.length === 1 && grIndex === 0 && (
														<Tooltip title="Add Row">
															<PlusCircleOutlined
																style={{
																	fontSize: '20px',
																	color: '#1890ff',
																	cursor: 'pointer',
																}}
																onClick={() => handleAddRow(index, grIndex)}
															/>
														</Tooltip>
													)}

													{/* Middle rows - only minus icon */}
													{subFields.length > 1 && grIndex > 0 && grIndex < subFields.length - 1 && (
														<Tooltip title="Delete Row">
															<MinusCircleOutlined
																style={{
																	fontSize: '20px',
																	color: '#ff4d4f',
																	cursor: 'pointer',
																}}
																onClick={() => {
																	subFieldsOpt.remove(subField.name);
																	handleDeleteRow(index);
																}}
															/>
														</Tooltip>
													)}

													{/* Last row (but not first) - both plus and minus icons */}
													{subFields.length > 1 && grIndex === subFields.length - 1 && (
														<>
															<Tooltip title="Add Row">
																<PlusCircleOutlined
																	style={{
																		fontSize: '20px',
																		color: '#1890ff',
																		marginRight: '12px',
																		cursor: 'pointer',
																	}}
																	onClick={() => handleAddRow(index, grIndex)}
																/>
															</Tooltip>
															<Tooltip title="Delete Row">
																<MinusCircleOutlined
																	style={{
																		fontSize: '20px',
																		color: '#ff4d4f',
																		cursor: 'pointer',
																	}}
																	onClick={() => {
																		subFieldsOpt.remove(subField.name);
																		handleDeleteRow(index);
																	}}
																/>
															</Tooltip>
														</>
													)}

													{/* First row with multiple rows - no icons */}
													{subFields.length > 1 && grIndex === 0 && <span></span>}
												</TableCell>
											)}
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={5} style={{ textAlign: 'center' }}>
											No data. Click plus icon in action column to add new entries.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Form.List>

			{/* Hidden form field to store processed data for API submission */}
			<Form.Item name={['leaseSimulator', index, 'grossRents']} noStyle />
			<Form.Item name={['leaseSimulator', index, 'leaseTerm']} noStyle />
		</>
	);
};
export default GrossRent;
